import React, { FC, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import {
  FilterBar,
  NotionDatePicker,
  PageScaffold,
  TabRow,
} from '@/apps/latest/components/portal/notion'
import {
  usePharmacyListQuery,
  usePharmacyUrlParam,
} from '@/features/internal/metrics'
import dayjs, { Dayjs } from 'dayjs'
import { useLocation, useNavigate } from 'react-router-dom'
import { SummaryTab } from './components/SummaryTab'
import { ComparisonTab } from './components/ComparisonTab'
import { RecordsTab } from './components/RecordsTab'
import {
  MetricsTarget,
  TargetSelector,
  encodePharmacyTarget,
} from './components/TargetSelector'

// タブは軸ではなく問いで切る:
//   概況「今どうなっているか」/ 比較「どこが・誰が際立つか」/ 明細「個々の実績は何か」
// (旧・利用統計/指導分析/指導実績一覧の3画面を統合。等価対応表はlog/179参照)
const TABS = [
  { id: 'overview', label: '概況' },
  { id: 'comparison', label: '比較' },
  { id: 'records', label: '明細' },
]

export const MetricsPage: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const tabParam = searchParams.get('tab')
  const activeTab = TABS.some((t) => t.id === tabParam)
    ? (tabParam as string)
    : 'overview'

  const { pharmacyId: selectedPharmacyId, updatePharmacyParam } =
    usePharmacyUrlParam()
  // 薬剤師軸の選択のみ画面内state。店舗/全社は `pharmacy_id` URLパラメータが真
  // (明細タブ・旧ページからの導線と対象を共有するため)
  const [pharmacistTarget, setPharmacistTarget] =
    useState<MetricsTarget | null>(null)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(1, 'month')
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())

  const { pharmacyData } = usePharmacyListQuery()

  // 対象の解決: 薬剤師選択中はそれを優先。それ以外はURLの店舗、なければ
  // 旧・利用統計と同じ既定 (本部権限=全社 / それ以外=先頭店舗)
  const target: MetricsTarget = useMemo(() => {
    if (pharmacistTarget) return pharmacistTarget
    if (selectedPharmacyId) return encodePharmacyTarget(selectedPharmacyId)
    if (!pharmacyData) return ''
    if (pharmacyData.has_headquarters_permission) return 'all'
    if (pharmacyData.pharmacies.length > 0) {
      return encodePharmacyTarget(pharmacyData.pharmacies[0].id)
    }
    // 所属店舗が特定できない場合は対象を指定しない (サーバ既定のスコープに委ねる)。
    // 'ph:' を送ると空文字の薬局IDになり、空の集計になる
    return pharmacyData.current_pharmacy_id
      ? encodePharmacyTarget(pharmacyData.current_pharmacy_id)
      : ''
  }, [pharmacistTarget, selectedPharmacyId, pharmacyData])

  const handleTabChange = (tabId: string) => {
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', tabId)
    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    })
  }

  const handleTargetChange = (value: MetricsTarget) => {
    if (value.startsWith('pt:')) {
      setPharmacistTarget(value)
      return
    }
    // 店舗/全社は pharmacy_id URLパラメータに反映し、明細タブとも対象を共有する
    setPharmacistTarget(null)
    updatePharmacyParam(value.startsWith('ph:') ? value.slice(3) : '')
  }

  const handleOpenRecords = (pharmacyId: string) => {
    // 店舗へのドリルダウンなので薬剤師選択は解除する
    // (残すと概況へ戻ったときURLの店舗と表示対象が食い違う)
    setPharmacistTarget(null)
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', 'records')
    newSearchParams.set('pharmacy_id', pharmacyId)
    navigate(`${location.pathname}?${newSearchParams.toString()}`)
  }

  return (
    <PageScaffold
      title="統計"
      // 明細は7列、 概況・比較は横並びのチャート/表。 720px だと画面を広げても中身は
      // 横スクロールのまま余白だけが増えるので contentMaxWide の例外を取る
      wide
      actions={
        <TabRow tabs={TABS} active={activeTab} onChange={handleTabChange} />
      }
    >
      {activeTab !== 'records' && (
        <FilterBar>
          {activeTab === 'overview' && (
            <Box sx={{ minWidth: 200 }}>
              <TargetSelector value={target} onChange={handleTargetChange} />
            </Box>
          )}
          <NotionDatePicker
            label="開始日"
            value={startDate}
            onChange={setStartDate}
          />
          <NotionDatePicker
            label="終了日"
            value={endDate}
            onChange={setEndDate}
          />
        </FilterBar>
      )}

      {activeTab === 'overview' && (
        <SummaryTab startDate={startDate} endDate={endDate} target={target} />
      )}
      {activeTab === 'comparison' && (
        <ComparisonTab
          startDate={startDate}
          endDate={endDate}
          onOpenRecords={handleOpenRecords}
        />
      )}
      {activeTab === 'records' && <RecordsTab />}
    </PageScaffold>
  )
}
